import { Box, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import type { SeriesDto } from '~/backend/connector-next-api/connector-next-schemas';
import {
    workflowStepById,
    type WorkflowStep,
    type WorkflowStepId,
} from '~/containers/viewer/explorer-sidebar/WorkflowStepper/workflow-steps';
import type { OpenIntent } from '~/hooks/openInViewer/resolveOpenIntent';
import { openableDatasetKind } from '../model';
import { PrimaryButton } from '../shared';

type DatasetOpenButtonProps = {
    series: SeriesDto;
    /** Connector dataset id; the row is not openable without one. */
    datasetId: string;
    /**
     * The workflow the patient's course stage leads to, or null for a course
     * that names none. Its action becomes the button — the user is most often
     * doing what the course says they are doing.
     */
    stageWorkflow: WorkflowStepId | null;
    disabled?: boolean;
    onOpen: (intent: OpenIntent) => void;
};

/** Steps a structure set can be opened into, in declaration order. */
const STRUCTURE_SET_STEPS: WorkflowStepId[] = ['contouring', 'review'];

/** Steps an image can be opened into, in declaration order. */
const IMAGE_STEPS: WorkflowStepId[] = ['contouring', 'registration', 'setup'];

/**
 * Steps a row can be opened into, the course stage's own first.
 *
 * The steps carry their own labels and icons, so a row offers "Contouring"
 * and the tab it lands on says "Contouring" — one table, read twice. Naming
 * them differently here would make the button a promise the destination does
 * not obviously keep.
 *
 * A dose, plan or registration opens into none of them: nothing displays an
 * RT object on its own, and the row used to be treated as an image simply for
 * not being a structure set — which offered Contouring on a registration and
 * addressed the open to a dataset no view can show.
 *
 * Exported for its test: the first step is the button, so this ordering is
 * what a single click commits to.
 */
export function datasetActions(
    kind: 'image' | 'structureSet' | null,
    stageWorkflow: WorkflowStepId | null
): WorkflowStep[] {
    if (kind === null) {
        return [];
    }
    const ids = kind === 'structureSet' ? STRUCTURE_SET_STEPS : IMAGE_STEPS;
    const ordered = [
        ...ids.filter((id) => id === stageWorkflow),
        ...ids.filter((id) => id !== stageWorkflow),
    ];
    return ordered
        .map((id) => workflowStepById(id))
        .filter((step): step is WorkflowStep => step !== undefined);
}

/**
 * Opens one dataset into a workflow, in one click: "contour this set",
 * "register this image".
 *
 * The button is the action the course stage points at, with the alternatives
 * behind the chevron — not a menu you have to open first. Choosing a dataset
 * and then choosing what to do with it are the same decision, made once, on
 * the row that names the thing.
 *
 * The intent names the object, so the resolver fills the rest from it rather
 * than from a heuristic: opening a structure set opens the image it was
 * contoured on, whichever image the patient's primary happens to be.
 */
export default function DatasetOpenButton({
    series,
    datasetId,
    stageWorkflow,
    disabled = false,
    onOpen,
}: DatasetOpenButtonProps) {
    const intl = useIntl();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    // An image or a structure set; a dose, plan or registration opens only
    // alongside the image it belongs to.
    const kind = openableDatasetKind(series);
    const [primary, ...alternatives] = datasetActions(kind, stageWorkflow);

    const open = (workflow: WorkflowStepId) => {
        setAnchor(null);
        onOpen({
            workflow,
            target:
                kind === 'structureSet'
                    ? { structureSet: datasetId }
                    : { image: datasetId },
        });
    };

    if (primary === undefined) {
        // Nothing this row opens into. The row itself says why, disabled and
        // with the reason on it; a bare button with no action would be worse
        // than none. Guarded here too so this can never render half a
        // control if the row's own test and this one ever disagree.
        return null;
    }

    return (
        <Box
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'stretch', gap: '1px' }}
        >
            <PrimaryButton
                disabled={disabled}
                onClick={() => open(primary.id)}
                sx={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
            >
                {intl.formatMessage({ id: primary.labelId })}
            </PrimaryButton>
            <PrimaryButton
                disabled={disabled}
                aria-haspopup="menu"
                aria-expanded={anchor !== null}
                aria-label={intl.formatMessage({
                    id: 'patientList.dataset.openIn',
                })}
                title={intl.formatMessage({ id: 'patientList.dataset.openIn' })}
                onClick={(event) => setAnchor(event.currentTarget)}
                sx={{
                    px: 0.5,
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                }}
            >
                <ChevronDown size={12} />
            </PrimaryButton>
            <Menu
                open={anchor !== null}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                MenuListProps={{ dense: true }}
            >
                {alternatives.map(({ id, labelId, icon: Icon }) => (
                    <MenuItem key={id} onClick={() => open(id)}>
                        <ListItemIcon>
                            <Icon />
                        </ListItemIcon>
                        <ListItemText
                            primary={intl.formatMessage({ id: labelId })}
                        />
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
}
